import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-d1dca488"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "flex flex-1 min-w-0" }
const _hoisted_2 = { class: "truncate" }
const _hoisted_3 = { class: "text-gray-600 font-semibold px-4 pt-2 pb-1 mr-4 flex items-center justify-between gap-2" }
const _hoisted_4 = { class: "truncate" }
const _hoisted_5 = {
  key: 0,
  class: "truncate"
}
const _hoisted_6 = {
  key: 2,
  class: "bg-yellow-400 px-1 py-0.5 text-xs rounded text-white"
}
const _hoisted_7 = {
  class: "flex gap-2 shrink-0",
  "data-oai-type": "actionButton"
}

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  ChevronDownIcon,
  PlusIcon,
  CheckIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/vue/24/outline";
import { TrashIcon, PencilIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import { computed, nextTick, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "shared/components/loading_state/LoadingSpinner.vue";
import OaiTooltip from "shared/components/other/OaiTooltip.vue";
import { useConfirmationModal } from "shared/composables/toast";
import { useTrackEvent } from "shared/composables/tracking";
import { useCreateOrUpdateDashboard, useDeleteDashboard } from "@/composables/dashboard";
import { Dashboard, DashboardType } from "@/types/Dashboard";
import DashboardNameModal from "@/views/dashboard/componentsV2/DashboardNameModal.vue";
import { useHasPermissionToEditDashboard } from "@/views/dashboard/composables";
import { createDashboardMenuCategories } from "@/views/dashboard/services/projectOverviewV2Layout";


export default /*#__PURE__*/_defineComponent({
  __name: 'DashboardMenu',
  props: {
    currentDashboard: {},
    dashboards: {},
    canEdit: { type: Boolean }
  },
  emits: ["selectDashboard", "showAddWidgetsSidebar", "resetCurrentDashboard", "refresh"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();

const showConfirmationModal = useConfirmationModal();
const { deleteDashboard, isLoading: isDashboardDeleting } = useDeleteDashboard();
const { createOrUpdateDashboard, isLoading: isDashboardCreatingOrUpdating } =
  useCreateOrUpdateDashboard();
const hasPermissionToEditDashboard = useHasPermissionToEditDashboard();

const dashboardNameModalType = ref<DashboardType | null>(null);
const nameEditingDashboard = ref<Dashboard | null>(null);
const nameEditingValue = ref("");
const nameEditingInputs = ref<HTMLInputElement[] | null>(null);

const categories = computed(() =>
  createDashboardMenuCategories(props.dashboards).map((category) => ({
    ...category,
    canAdd: props.canEdit && hasPermissionToEditDashboard(category),
  })),
);

const trackEvent = useTrackEvent();

const handleDeleteDashboardClick = (dashboard: Dashboard) => {
  if (isDashboardDeleting.value) {
    return;
  }
  showConfirmationModal({
    confirmAction: t("dashboard.delete_confirmation.confirm"),
    cancelAction: t("dashboard.delete_confirmation.cancel"),
    message: t("dashboard.delete_confirmation.message", { name: dashboard.name }),
    header: t("dashboard.delete_confirmation.header"),
  }).then((confirmed) => {
    if (confirmed) {
      // have to catch the error here, otherwise it's propagated as an unhandled error
      // it's already logged by vue-query
      deleteDashboard(dashboard._id).catch(() => undefined);
    }
  });
  trackEvent("dashboard_delete", {
    type: dashboard.type,
  });
};

const canDeleteDashboard = (dashboard: Dashboard) =>
  hasPermissionToEditDashboard(dashboard) && !dashboard.default;

const canRenameDashboard = (dashboard: Dashboard) =>
  hasPermissionToEditDashboard(dashboard) && !dashboard.generated;

const eventIsFromActionButton = (element: HTMLElement): boolean => {
  if (element.dataset.oaiType === "actionButton") {
    return true;
  }
  if (element.parentElement) {
    return eventIsFromActionButton(element.parentElement);
  }
  return false;
};

const handleMenuItemClick = (event: Event, dashboard: Dashboard) => {
  const source = event.target as HTMLElement;
  if (eventIsFromActionButton(source)) {
    return;
  }
  emit("selectDashboard", dashboard);
};

const resetNameEditing = () => {
  nameEditingDashboard.value = null;
  nameEditingValue.value = "";
};

const handleEditNameClick = (dashboard: Dashboard) => {
  nameEditingDashboard.value = dashboard;
  nameEditingValue.value = dashboard.name;
  nextTick(() => {
    if (nameEditingInputs.value) {
      nameEditingInputs.value[0]?.focus();
    }
  });
};

const handleConfirmNameClick = () => {
  if (!nameEditingDashboard.value || !nameEditingValue.value) {
    return;
  }
  createOrUpdateDashboard({
    ...nameEditingDashboard.value,
    name: nameEditingValue.value,
    version: nameEditingDashboard.value.version + 1,
  })
    .then(() => {
      resetNameEditing();
    })
    // have to catch the error here, otherwise it's propagated as an unhandled error
    // it's already logged by vue-query
    .catch(() => undefined);
  trackEvent("dashboard_rename_apply", { type: nameEditingDashboard.value.type });
};

watch(
  () => props.dashboards,
  () => {
    resetNameEditing();
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(Menu), {
        as: "div",
        class: "flex relative min-w-0",
        id: "dashboard-menu"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(MenuButton), {
            type: "button",
            class: "flex-1 flex items-center font-semibold text-gray-700 max-w-[300px] min-w-0 outline-none",
            onClick: resetNameEditing
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, _toDisplayString(_ctx.currentDashboard?.name || _unref(t)("dashboard.default_dashboard")), 1),
              _createVNode(_unref(ChevronDownIcon), { class: "ml-2 h-5 w-5 text-gray-400 shrink-0" })
            ]),
            _: 1
          }),
          _createVNode(_unref(MenuItems), {
            class: "absolute top-6 z-50 mt-3 origin-top-right rounded-md bg-white shadow-lg focus:outline-none flex flex-col gap-3 py-2 w-[300px] lg:w-[350px] overflow-auto dashboardMenuItems",
            style: { left: '-2px' }
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(categories.value, (category) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: category.type
                }, [
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)(`dashboard.menu_categories.${category.type}`)), 1),
                    (category.canAdd)
                      ? (_openBlock(), _createBlock(_unref(MenuItem), {
                          key: 0,
                          onClick: ($event: any) => (dashboardNameModalType.value = category.type),
                          style: { marginRight: '-2px' }
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(PlusIcon), { class: "w-5 h-5 cursor-pointer shrink-0 outline-none" })
                          ]),
                          _: 2
                        }, 1032, ["onClick"]))
                      : _createCommentVNode("", true)
                  ]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(category.dashboards, (dashboard) => {
                    return (_openBlock(), _createBlock(_unref(MenuItem), {
                      key: dashboard._id,
                      onClick: ($event: any) => (handleMenuItemClick($event, dashboard))
                    }, {
                      default: _withCtx(({ active }) => [
                        _createElementVNode("div", {
                          class: _normalizeClass(["px-4 py-2 text-sm truncate cursor-pointer flex items-center gap-4 mx-4 rounded", {
                'bg-gray-100': active,
                'bg-yellow-300': _ctx.currentDashboard?._id === dashboard._id,
              }])
                        }, [
                          _createElementVNode("div", {
                            class: _normalizeClass(["flex-1 flex gap-2 min-w-0", 
                  active && _ctx.currentDashboard?._id !== dashboard._id
                    ? 'text-yellow-600'
                    : 'text-gray-700'
                ])
                          }, [
                            (nameEditingDashboard.value?._id !== dashboard._id)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(dashboard.name), 1))
                              : _withDirectives((_openBlock(), _createElementBlock("input", {
                                  key: 1,
                                  ref_for: true,
                                  ref_key: "nameEditingInputs",
                                  ref: nameEditingInputs,
                                  type: "text",
                                  autocomplete: "one-time-code",
                                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((nameEditingValue).value = $event)),
                                  onKeydown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"])),
                                  onKeyup: [
                                    _withKeys(handleConfirmNameClick, ["enter"]),
                                    _withKeys(resetNameEditing, ["esc"])
                                  ],
                                  onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                                  class: "-mx-1 -my-1 block w-full rounded-md border-0 py-1 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-yellow-600 sm:text-sm sm:leading-6"
                                }, null, 544)), [
                                  [_vModelText, nameEditingValue.value]
                                ]),
                            (dashboard.default && nameEditingDashboard.value?._id !== dashboard._id)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_6, " default "))
                              : _createCommentVNode("", true)
                          ], 2),
                          _createElementVNode("div", _hoisted_7, [
                            (
                    (active || dashboard._id === _ctx.currentDashboard?._id) &&
                    canRenameDashboard(dashboard) &&
                    !nameEditingDashboard.value &&
                    !_unref(isDashboardCreatingOrUpdating)
                  )
                              ? (_openBlock(), _createBlock(_unref(PencilIcon), {
                                  key: 0,
                                  class: "w-4 h-4 cursor-pointer text-gray-600 hover:text-yellow-600",
                                  onClick: _withModifiers(($event: any) => (handleEditNameClick(dashboard)), ["stop"])
                                }, null, 8, ["onClick"]))
                              : _createCommentVNode("", true),
                            (
                    nameEditingDashboard.value?._id === dashboard._id && !_unref(isDashboardCreatingOrUpdating)
                  )
                              ? (_openBlock(), _createBlock(_unref(CheckIcon), {
                                  key: 1,
                                  class: "w-5 h-5 cursor-pointer text-gray-600 hover:text-yellow-600",
                                  onClick: _withModifiers(handleConfirmNameClick, ["stop"])
                                }))
                              : _createCommentVNode("", true),
                            (
                    nameEditingDashboard.value?._id === dashboard._id && !_unref(isDashboardCreatingOrUpdating)
                  )
                              ? (_openBlock(), _createBlock(_unref(XMarkIcon), {
                                  key: 2,
                                  class: "w-5 h-5 cursor-pointer text-gray-600 hover:text-yellow-600",
                                  onClick: _withModifiers(resetNameEditing, ["stop"])
                                }))
                              : _createCommentVNode("", true),
                            (
                    nameEditingDashboard.value?._id === dashboard._id && _unref(isDashboardCreatingOrUpdating)
                  )
                              ? (_openBlock(), _createBlock(LoadingSpinner, {
                                  key: 3,
                                  size: "w-5 h-5"
                                }))
                              : _createCommentVNode("", true),
                            (
                    (active || dashboard._id === _ctx.currentDashboard?._id) &&
                    !nameEditingDashboard.value &&
                    _ctx.canEdit &&
                    _unref(hasPermissionToEditDashboard)(dashboard) &&
                    dashboard._id === _ctx.currentDashboard?._id &&
                    !_unref(isDashboardCreatingOrUpdating) &&
                    !_unref(isDashboardDeleting)
                  )
                              ? (_openBlock(), _createBlock(OaiTooltip, {
                                  key: 4,
                                  position: "right"
                                }, {
                                  tooltip: _withCtx(() => [
                                    _createElementVNode("div", null, _toDisplayString(_unref(t)("dashboard.reset_dashboard")), 1)
                                  ]),
                                  default: _withCtx(() => [
                                    _createVNode(_unref(ArrowUturnLeftIcon), {
                                      class: "w-4 h-4 shrink-0 cursor-pointer hover:text-yellow-600",
                                      onClick: _cache[3] || (_cache[3] = ($event: any) => (emit('resetCurrentDashboard')))
                                    })
                                  ]),
                                  _: 1
                                }))
                              : _createCommentVNode("", true),
                            (
                    (active || dashboard._id === _ctx.currentDashboard?._id) &&
                    canDeleteDashboard(dashboard) &&
                    nameEditingDashboard.value?._id !== dashboard._id
                  )
                              ? (_openBlock(), _createBlock(_unref(TrashIcon), {
                                  key: 5,
                                  onClick: ($event: any) => (handleDeleteDashboardClick(dashboard)),
                                  class: _normalizeClass(["w-4 h-4 cursor-pointer", 
                    _unref(isDashboardDeleting) ? 'text-gray-300' : 'text-gray-600 hover:text-yellow-600'
                  ])
                                }, null, 8, ["onClick", "class"]))
                              : _createCommentVNode("", true)
                          ])
                        ], 2)
                      ]),
                      _: 2
                    }, 1032, ["onClick"]))
                  }), 128))
                ]))
              }), 128))
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createTextVNode(" " + _toDisplayString(dashboardNameModalType.value), 1)
    ]),
    (dashboardNameModalType.value)
      ? (_openBlock(), _createBlock(DashboardNameModal, {
          key: 0,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (dashboardNameModalType.value = null)),
          dashboardNameModalType: dashboardNameModalType.value
        }, null, 8, ["dashboardNameModalType"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})